import StatusCode from 'status-code-enum';
import getConfig from 'next/config';
import { isAxiosError } from 'axios';

import { logger } from '@dist-marketplace-frontend/services';

import { adViewApi } from '../../../../api/adViewApi';
import { fireToast } from '../../../../helpers/Toasts';
import { pushToDataLayer } from '../../../../services/gtmService';
import { useScript } from '../../../../hooks/UseScript';
import { getRecaptchaToken } from '../../../../helpers/reCaptcha';
import { useBraze } from '../../../../contexts/BrazeContext';
import { useUserContext } from '../../../../contexts/UserContext';
import type { SellerTypes } from '../../../../components/PageWrappers/AdDetails/types';
import { PAGE } from '../../../../helpers/pages';
import { useAdDetailsLeadTracking } from '../../../../helpers/experiments/tracking/useAdDetailsLeadTracking';

interface UseRevealPhoneNumberProps {
  adId: number;
  clickSource?: string;
  campaignId?: string | null;
  sectionName?: string;
  sellerType?: SellerTypes;
  price?: string | null;
}

function useRevealPhoneNumber({
  adId,
  clickSource = '',
  campaignId,
  sectionName,
  sellerType,
  price,
}: UseRevealPhoneNumberProps) {
  const {
    publicRuntimeConfig: { GOOGLE_RECAPTCHA },
  } = getConfig();

  useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`,
    'recaptcha',
    [adId],
  );

  const { handleLogin, accessToken } = useUserContext();
  const { logBrazeRevealPhoneEvent } = useBraze();
  const { trackAdDetailsLead } = useAdDetailsLeadTracking({
    page: PAGE.AD_DETAILS,
  });

  async function handleReveal() {
    try {
      const captchaToken = await getRecaptchaToken('phone_reveal');
      const { data } = await adViewApi.getPhoneNumber(
        adId,
        captchaToken,
        campaignId,
        accessToken,
      );

      pushToDataLayer({ event: 'phone_reveal', source: clickSource });
      logBrazeRevealPhoneEvent(sectionName, sellerType);
      if (!data.valid) {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
        return false as const;
      }
      return data.displayFormat;
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === StatusCode.ClientErrorUnauthorized
      ) {
        handleLogin();
      } else {
        logger.error({
          error,
          tags: [PAGE.AD_DETAILS],
          message: 'Error revealing phone number',
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
      return false as const;
    }
  }

  async function handleOnCall() {
    try {
      pushToDataLayer({ event: 'click_to_call', source: clickSource });
      trackAdDetailsLead({ sectionName, sellerType, price });
      await adViewApi.trackAdCallNumber(adId, campaignId);
    } catch (error) {
      logger.error({
        error,
        message: 'Error tracking call to phone number',
        tags: [PAGE.AD_DETAILS],
      });
    }
  }

  return { handleReveal, handleOnCall };
}

export { useRevealPhoneNumber, type UseRevealPhoneNumberProps };
